import { useState } from "react";
import { FileSpreadsheet, Loader2, Download, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const COLS = ["Nom", "Post-nom", "Prénom", "Sexe (M/F)", "Téléphone", "Province", "Lieu d'exercice", "Numéro CNAP", "Date de délivrance carte verte (JJ/MM/AAAA)"];

const download = async () => {
  try {
    const r = await api.get("/admin/aps/import-template", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "modele_import_ap.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error("Échec du téléchargement"); }
};

const Report = ({ res }) => (
  <div data-testid="import-result" className="space-y-3">
    <div className="flex items-center gap-2 text-emerald-800 font-semibold"><CheckCircle2 className="h-5 w-5" /> <span data-testid="import-created-count">{res.importes}</span> AP importé(s)</div>
    {res.erreurs.length > 0 && (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
        <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm"><AlertTriangle className="h-4 w-4" /> <span data-testid="import-error-count">{res.erreurs.length}</span> ligne(s) ignorée(s)</div>
        <ul data-testid="import-errors-list" className="mt-2 max-h-48 overflow-y-auto text-xs text-amber-900 space-y-1">
          {res.erreurs.map((e, i) => <li key={i}>Ligne {e.ligne}{e.cnap ? ` (CNAP ${e.cnap})` : ""} : {e.raison}</li>)}
        </ul>
      </div>
    )}
  </div>
);

export const ImportDialog = ({ open, onOpenChange, onDone }) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const close = (o) => { if (!o) { setFile(null); setRes(null); } onOpenChange(o); };

  const submit = async () => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/aps/import", fd);
      setRes(data);
      if (data.importes) onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="import-dialog" aria-describedby={undefined} className="max-w-xl">
        <DialogHeader><DialogTitle className="font-display">Importer des AP depuis Excel</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="rounded-xl bg-emerald-50/60 border border-emerald-900/10 p-4">
            <div className="font-semibold text-slate-800 mb-2">Colonnes prises en charge (1<sup>re</sup> ligne = en-têtes) :</div>
            <div className="flex flex-wrap gap-1.5">{COLS.map((c) => <span key={c} className="rounded-full bg-white border border-emerald-900/10 px-2 py-0.5 text-xs">{c}</span>)}</div>
            <p className="text-xs text-slate-500 mt-2">« Nom », « Sexe » et « Numéro CNAP » (8 chiffres) sont obligatoires. Les numéros CNAP déjà enregistrés sont ignorés.</p>
            <button type="button" onClick={download} data-testid="import-template-download" className="mt-3 inline-flex items-center gap-1 text-emerald-800 font-semibold hover:underline"><Download className="h-4 w-4" /> Télécharger le modèle Excel</button>
          </div>
          <label className="flex items-center gap-3 rounded-xl border-2 border-dashed border-emerald-900/15 p-4 cursor-pointer hover:bg-emerald-50/50 transition-colors">
            <FileSpreadsheet className="h-6 w-6 text-emerald-700 shrink-0" />
            <span data-testid="import-file-name" className="truncate">{file ? file.name : "Choisir un fichier .xlsx"}</span>
            <input type="file" accept=".xlsx" data-testid="import-file-input" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setRes(null); e.target.value = ""; }} />
          </label>
          {res && <Report res={res} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} data-testid="import-close">Fermer</Button>
          <Button onClick={submit} disabled={!file || busy} data-testid="import-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Importer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
